import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UserAccount, CreateUserRequest, UpdateUserRequest } from '../../types/user';
import { X, Eye, EyeOff, Save, UserPlus } from 'lucide-react';

interface UserFormProps {
  user?: UserAccount | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => void;
  loading?: boolean;
}

// Create separate schemas for create and edit
const createUserSchema = yup.object({
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().email('Invalid email format').nullable(),
  full_name: yup.string().nullable(),
  role: yup.string().oneOf(['manager', 'executive1', 'executive2', 'worker'], 'Invalid role').required('Role is required'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  is_active: yup.boolean()
});

const updateUserSchema = yup.object({
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().email('Invalid email format').nullable(),
  full_name: yup.string().nullable(),
  role: yup.string().oneOf(['manager', 'executive1', 'executive2', 'worker'], 'Invalid role').required('Role is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').nullable().transform((value) => value || null),
  is_active: yup.boolean()
});

const roleOptions = [
  { value: 'manager', label: 'Manager', description: 'Full system access with administrative privileges' },
  { value: 'executive1', label: 'Executive 1', description: 'Order processing and inventory viewing' },
  { value: 'executive2', label: 'Executive 2', description: 'Stock management and order verification' },
  { value: 'worker', label: 'Worker', description: 'Order picking and task management' }
];

const UserForm: React.FC<UserFormProps> = ({ user, isOpen, onClose, onSubmit, loading = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isEditing = !!user;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(isEditing ? updateUserSchema : createUserSchema),
    defaultValues: {
      username: '',
      email: '',
      full_name: '',
      role: 'worker' as const,
      password: '',
      is_active: true
    }
  });
  useEffect(() => {
    if (user) {
      reset({
        username: user.username,
        email: user.email || '',
        full_name: user.full_name || '',
        role: user.role,
        password: '',
        is_active: user.is_active
      });
    } else {
      reset({
        username: '',
        email: '',
        full_name: '',
        role: 'worker',
        password: '',
        is_active: true
      });
    }
  }, [user, reset]);

  const selectedRole = watch('role');
  const selectedRoleInfo = roleOptions.find(option => option.value === selectedRole);
  const onFormSubmit = (data: any) => {
    if (isEditing) {
      // For editing, exclude password from the update request
      const { password, ...updateData } = data;
      const updateRequest: UpdateUserRequest = {
        id: user!.id,
        ...updateData
      };
      onSubmit(updateRequest);
    } else {
      // For creating, include all data
      onSubmit(data as CreateUserRequest);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit User' : 'Create New User'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username *
              </label>
              <input
                {...register('username')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter username"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                {...register('email')}
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter email address (optional)"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              {...register('full_name')}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter full name (optional)"
            />
            {errors.full_name && (
              <p className="mt-1 text-sm text-red-600">{errors.full_name.message}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role *
            </label>
            <select
              {...register('role')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedRoleInfo && (
              <p className="mt-1 text-sm text-gray-500">{selectedRoleInfo.description}</p>
            )}
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>          {/* Password - only show for new users */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Minimum 6 characters
              </p>
            </div>
          )}

          {/* Password change note for editing */}
          {isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-800">
                To change the password for this user, use the separate "Change Password" option in the user actions menu.
              </p>
            </div>
          )}

          {/* Active Status */}
          <div className="flex items-center">
            <input
              {...register('is_active')}
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Active User
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : isEditing ? (
                <Save className="h-4 w-4 mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Saving...' : isEditing ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;
